import React, { useEffect, useMemo, useRef } from 'react';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Skeleton } from '@lifeomic/chroma-react/components/Skeleton';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import { Procedure } from '@lifeomic/chromicons';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { useStyles } from '../styles';
import { MasterDetailProps } from '../types';

function AuditMasterDetail<T>(props: MasterDetailProps<T>) {
  const {
    rows,
    loading,
    emptyMessage,
    noSelectionMessage,
    rowId,
    listColumns,
    detailTitle,
    detailRows,
    selectedRowId,
    setSelectedRowId,
  } = props;
  const styles = useStyles();
  const tableRegionRef = useRef<HTMLDivElement | null>(null);

  const selectedRow = useMemo(() => {
    if (!selectedRowId) {
      return rows[0] || null;
    }
    return rows.find(row => rowId(row) === selectedRowId) || rows[0] || null;
  }, [rows, rowId, selectedRowId]);

  useEffect(() => {
    const nextSelectedRowId = selectedRow ? rowId(selectedRow) : null;
    if (nextSelectedRowId === selectedRowId) {
      return;
    }

    if (selectedRow) {
      setSelectedRowId(nextSelectedRowId);
    } else {
      setSelectedRowId(null);
    }
  }, [selectedRow, rowId, selectedRowId, setSelectedRowId]);

  useEffect(() => {
    const tableRegion = tableRegionRef.current;
    if (!tableRegion) {
      return;
    }

    const allRows = tableRegion.querySelectorAll('[data-selected="true"]');
    allRows.forEach(row => row.removeAttribute('data-selected'));

    if (selectedRowId) {
      const selectedIndex = rows.findIndex(row => rowId(row) === selectedRowId);
      if (selectedIndex !== -1) {
        const tableRows = tableRegion.querySelectorAll(
          `.${styles.tableWrapper} tbody tr`,
        );
        const selectedElement = tableRows[selectedIndex];
        if (selectedElement) {
          selectedElement.setAttribute('data-selected', 'true');
        }
      }
    }
  }, [styles.tableWrapper, rowId, rows, selectedRowId]);

  if (loading) {
    return (
      <Box className={styles.auditMasterDetailRoot}>
        <Skeleton width="100%" height="100%" className={styles.skeleton} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box className={styles.auditMasterDetailRoot}>
        <Box
          className={styles.mainContentEmpty}
          direction="row"
          align="center"
          justify="center"
        >
          <EmptyPageState
            illustration={Procedure}
            message={<Text size="subbody">{emptyMessage}</Text>}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.auditMasterDetailRoot}>
      <Box className={styles.bodyContent}>
        <div className={styles.tableScrollContainer} ref={tableRegionRef}>
          <div className={`${styles.tableWrapper} ${styles.auditTableWrapper}`}>
            <TableModule
              config={listColumns}
              data={rows}
              isLoading={false}
              wrapCellContent
              onRowClick={(row: T) => setSelectedRowId(rowId(row))}
              rowClickLabel="View details"
            />
          </div>
        </div>
        <Box className={styles.readingPane}>
          {!selectedRow ? (
            <Box className={styles.emptyPaper}>
              <EmptyPageState
                illustration={Procedure}
                message={<Text size="subbody">{noSelectionMessage}</Text>}
              />
            </Box>
          ) : (
            <Box direction="column" className={styles.detailPaper}>
              <Box className={styles.readingPaneHeader}>
                <Text size="subbody" weight="bold">
                  {detailTitle(selectedRow)}
                </Text>
                <Divider className={styles.divider} />
              </Box>
              <Box className={styles.detailBody} direction="column" gap={1}>
                {detailRows(selectedRow).map(item => (
                  <Box key={item.label} direction="row" gap={1}>
                    <Text size="table" weight="bold">
                      {item.label}:
                    </Text>
                    {typeof item.value === 'string' ? (
                      <Text size="table" style={{ whiteSpace: 'pre-wrap' }}>
                        {item.value}
                      </Text>
                    ) : (
                      item.value
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default AuditMasterDetail;
